import { ArrayBufferTarget, Muxer } from 'mp4-muxer'
import {
  ArrayBufferTarget as WebmArrayBufferTarget,
  Muxer as WebmMuxer,
} from 'webm-muxer'
import { COMET_CYCLE_MS } from '../models/hsiCometFrame'
import { composeFrame, download, framePixels, pixelsDiffer } from './exportImage'
import { activeBasemap } from './baseStyle'
import { prepareAttribution } from './attribution/exportAttribution'
import { getLang } from '../i18n'
import { makeClock, timingSummary } from './exportTiming'
import type { ExportContext } from './exportPlate'
import type { TimerFrame } from './exportTimer'
import type { GraticuleFrame } from './graticule'
import type { VideoFrameSpec } from './videoTimeline'

/**
 * Запись ролика в MP4 (H.264).
 *
 * Ролик идёт ПО СУТКАМ окна перемотки: точки выловов и косяков едут по своим
 * трекам вместе с ореолами погрешности, растр меняется на смене даты, внизу
 * кадра стоит таймер. Раскладку кадров (какой кадр за какие сутки) считает
 * `videoTimeline`, а какие сутки вообще попадут в файл — решает страница по
 * готовности слоя (`models/videoDates`). Здесь — только кодирование.
 *
 * Три решения, из которых растёт весь модуль.
 *
 * 1. **Фазу задаёт запись, а не стенные часы.** Раньше кадры снимались «через
 *    равные паузы», пока анимация свободно бежала от `performance.now()`: реальный
 *    отрезок выходил длиннее цикла на время самого снятия, и на стыке было видно
 *    наложение. Теперь кадр k рисуется ровно при `clock = k/N`, k = 0…N−1.
 *    Комета периодична по такту (`(clock + phase) % 1` в hsiCometFrame), поэтому
 *    кадр, следующий за последним, СОВПАЛ БЫ с первым — шов нулевой по построению,
 *    а не подогнан на глаз. Фазы 1 в записи нет: она и есть первый кадр.
 *
 * 2. **MP4 вместо GIF.** GIF-палитра — 256 цветов на весь кадр: карта в градиенте
 *    осыпалась в полосы, а файл всё равно весил мегабайты. H.264 отдаёт истинный
 *    цвет в полном разрешении и весит в разы меньше.
 *
 * Кодирует браузер (WebCodecs `VideoEncoder`), в контейнер складывает mp4-muxer.
 * Без WebCodecs кнопка честно говорит, что этот браузер записывать не умеет, —
 * молчаливый фолбэк в GIF вернул бы ровно ту картинку, от которой уходим.
 *
 * **WebM — запасной, а не равный.** Firefox на Linux отвергает все четыре
 * профиля `avc1`: WebCodecs опирается там на системный кодировщик, а H.264
 * Mozilla не поставляет, — и человек получал отказ на ровном месте. Значит
 * пишем ему VP9 в WebM. Именно ЗАПАСНОЙ: MP4 открывается везде и остаётся
 * первым выбором, а VP9 в mp4-контейнере (формально допустимый) не открылся бы
 * у коллеги на маке — файл, который нельзя переслать, хуже отказа.
 *
 * **Слову браузера про H.264 мы не верим — спрашиваем первый чанк САМОЙ ЗАПИСИ.**
 * Тот же Firefox на WINDOWS отвечает на `isConfigSupported('avc1.640028')`
 * согласием (там за WebCodecs стоит системный Media Foundation), кодирует — и не
 * отдаёт в метаданных `decoderConfig`. Собирать mp4 не из чего: муксер падал на
 * `can't access property "colorSpace"`, а запасная дорожка не включалась вовсе,
 * потому что H.264 формально «поддержан» (бой 16.08.2026).
 *
 * Первая попытка чинить это отдельной пробой была неверна дважды. Во-первых,
 * пробу вёл ВТОРОЙ экземпляр кодировщика — а он и есть предмет проверки:
 * ответил бы он иначе, чем боевой, и мы поверили бы не тому. Во-вторых, отказ
 * муксера после старта записи оставался терминальным, и WebM, формально живой,
 * был недостижим.
 *
 * Поэтому дорожки выстроены в ОЧЕРЕДЬ (`trackPlan`), первый чанк придерживается
 * до вердикта (`openSession`), а переезд на следующую дорожку стоит одного
 * повторного кадра. Позже нельзя: подготовка суток (`anim.begin`) занимает до
 * минуты и пересдаче не подлежит.
 */

/** Частота записи. 30 к/с: кометы идут плавно, а цикл делится нацело. */
export const VIDEO_FPS = 30

/** Кадров ровно на ОДИН пролёт кометы — длина ролика равна длине цикла. */
export const VIDEO_FRAMES = Math.round((COMET_CYCLE_MS / 1000) * VIDEO_FPS)

/** Потолок ширины, px. Кадр retina-вьюпорта (3200 px) кодировщику не нужен:
 *  вес растёт вчетверо, а разглядеть на нём нечего. */
export const VIDEO_MAX_WIDTH = 1920

/** Профили H.264 от лучшего к запасному. High даёт лучшую картинку на тот же
 *  вес; 4.0 покрывает 1920×1080@30, ниже — запас для старых машин. */
export const CODEC_CANDIDATES = [
  'avc1.640028', // High 4.0
  'avc1.4d0028', // Main 4.0
  'avc1.42e028', // Constrained Baseline 4.0
  'avc1.42001f', // Baseline 3.1
] as const

/** Запасные профили, если H.264 браузер не кодирует. VP9 лучше жмёт, VP8 —
 *  для машин постарше. Оба живут в WebM, а не в MP4: VP9 в mp4-контейнере
 *  формально допустим, но Safari и QuickTime его не открывают — файл, записанный
 *  на Firefox, не открылся бы у коллеги на маке, и это было бы хуже отказа. */
export const WEBM_CODEC_CANDIDATES = ['vp09.00.10.08', 'vp8'] as const

/** Контейнер записи. */
export type Container = 'mp4' | 'webm'

/** Чем и во что писать. */
export interface VideoTrack {
  codec: string
  container: Container
}

/** Чем кончилась запись. */
export type VideoResult =
  | 'saved'
  | 'no-motion'
  | 'unsupported'
  | 'no-frames'
  | 'no-dates'
  | 'failed'

/** Что показать человеку по итогу записи. `null` — файл сохранён, молчим. */
export type ExportNote = 'failed' | 'no-motion' | 'unsupported' | 'no-dates' | null

/** Исход записи → сообщение.
 *
 *  Разводить исходы обязательно, а не желательно. «Не удалось сохранить —
 *  попробуйте ещё раз» на браузере, который H.264 не кодирует ВООБЩЕ, — совет,
 *  который не сработает никогда: Firefox на Windows отвергает все четыре
 *  профиля `avc1`, и повтор ничего не меняет. Отказ кодировщика посреди записи —
 *  другое дело, там повтор осмыслен.
 */
export function exportNote(result: VideoResult): ExportNote {
  if (result === 'saved') return null
  if (result === 'unsupported') return 'unsupported'
  if (result === 'no-motion') return 'no-motion'
  // Ни одних готовых суток — повторять нечего: слоя на текущую дату нет ни в
  // тайлах, ни в базе, и лечится это кнопкой «Загрузить», а не вторым «MP4».
  if (result === 'no-dates') return 'no-dates'
  return 'failed'
}

/** Где именно оборвалась запись.
 *
 *  Стадии обязаны РАЗЛИЧАТЬСЯ, потому что лечатся по-разному. `encode` — отказ
 *  кодировщика, тут повтор осмыслен. `mux` — кодировщик отдал чанк, а контейнер
 *  его не принял: повтор бесполезен, помогает смена контейнера, и мы её делаем
 *  сами. `save` — писать было нечем (пустой контейнер). `probe` — след
 *  пересадки: первый чанк боевого кодировщика пришёл без avcC, и запись ушла на
 *  запасную дорожку; сама по себе это не ошибка, но в строке она должна быть
 *  видна, иначе непонятно, почему файл вышел .webm.
 *
 *  Раньше `finalize()` помечался как `encode`, а отказ муксера — как `save`, и
 *  по метке нельзя было отличить «браузер не кодирует» от «контейнер не принял»,
 *  то есть понять, поможет ли смена контейнера. `probe` не использовался вовсе.
 */
export type FailureStage =
  | 'codec' | 'probe' | 'configure' | 'encode' | 'mux' | 'save'

/** Причина отказа одной строкой — её человек перешлёт, а мы прочитаем.
 *
 *  Заводится потому, что причину МЫ ЖЕ И ТЕРЯЛИ: колбэк `error` кодировщика
 *  ронял объект ошибки на пол, и все исходы сходились к одной фразе «не удалось
 *  сохранить». Отказ на чужой машине после этого неотличим ни от чего: консоль
 *  не наша, серверного следа у клиентской записи нет.
 *
 *  Тот же приём, что у `error.sectionCrashedHint`: показать текст и попросить
 *  переслать — дешевле отдельного канала телеметрии и работает сразу.
 *
 *  Строка НЕ переводится и в каталог не идёт намеренно: это техническая метка
 *  для разбора, а не подпись интерфейса. Отсюда нейтральные токены
 *  (`stage=`, `frame=`) — их одинаково читают и по-русски, и по-английски.
 */
export function failureDetail(input: {
  stage: FailureStage
  size: { width: number; height: number }
  codec?: string | null
  error?: unknown
}): string {
  const parts = [
    `stage=${input.stage}`,
    `frame=${input.size.width}×${input.size.height}`,
  ]
  if (input.stage === 'codec') parts.push(`codecs=${CODEC_CANDIDATES.length}`)
  if (input.codec) parts.push(input.codec)
  const err = input.error
  if (err instanceof Error) {
    parts.push(`${err.name}: ${err.message}`)
  } else if (err !== undefined && err !== null) {
    parts.push(String(err))
  }
  return parts.join(' · ')
}

/** Управление анимацией на время записи. Пропа нет — анимировать нечего. */
export interface ExportAnimation {
  /** Есть что анимировать: усики HSI или видимый снос косяков и выловов. */
  ready: boolean
  /** Ввести карту в режим записи и отдать раскладку ролика.
   *
   *  Раскладку отдаёт СТРАНИЦА, а не запись: какие сутки попадут в ролик, знает
   *  только она — это ответ готовности слоя по каждой дате окна. Пустой список
   *  означает «готовых суток нет», и это отдельный исход, а не отказ кодировщика.
   *
   *  `onProgress` — та же полоска, что у самой записи: подготовка может занять
   *  минуту (дорисовка недостающих дат), и застывшая на нуле кнопка читалась бы
   *  как «не нажалось». */
  begin: (onProgress?: (share: number) => void) => Promise<VideoFrameSpec[]>
  /** Что написать на таймере этого кадра. `null` — таймера в кадре нет. */
  timerFor: (spec: VideoFrameSpec) => TimerFrame | null
  /** Нарисовать кадр (сутки, доля суток, фаза кометы) и дождаться карты. */
  renderFrame: (spec: VideoFrameSpec) => Promise<void>
  /** Вернуть экран в то состояние, в котором его застали. */
  end: () => void
  /** Какие сутки попали в файл — одной фразой, уже на языке кабинета.
   *
   *  Обязателен ровно потому, что окно записи может оказаться короче окна
   *  перемотки: умолчав, мы оставили бы человека в уверенности, что ролик накрыл
   *  всё, — и он не пошёл бы догружать недостающие даты. */
  coverageLabel?: () => string | null
}

/** Метка времени кадра, мкс. */
export function frameTimestampUs(index: number): number {
  return Math.round((index * 1e6) / VIDEO_FPS)
}

/** Размер кадра для кодировщика: не шире потолка и с ЧЁТНЫМИ сторонами —
 *  H.264 нечётных не принимает. */
export function videoSize(
  width: number,
  height: number,
  maxWidth: number = VIDEO_MAX_WIDTH,
): { width: number; height: number } {
  const scale = Math.min(1, maxWidth / Math.max(1, width))
  const even = (n: number) => Math.max(2, Math.round(n) - (Math.round(n) % 2))
  return { width: even(width * scale), height: even(height * scale) }
}

/** Метаданные первого чанка, какими их отдаёт кодировщик. */
export interface ChunkMeta {
  decoderConfig?: { description?: unknown } | null
}

/** Годны ли метаданные для дорожки H.264 в mp4.
 *
 *  Требуется `decoderConfig.description` — это и есть avcC (SPS/PPS), который
 *  муксер кладёт в заголовок дорожки. Без него файла нет: mp4-muxer читает
 *  `decoderConfig.colorSpace` и валится с TypeError, а если бы и не валился —
 *  дорожка без SPS/PPS невоспроизводима.
 *
 *  Пустой `description` считается отсутствующим: нулевой avcC — тот же нечитаемый
 *  файл, только без честного отказа. */
export function avcMetaUsable(meta: ChunkMeta | null | undefined): boolean {
  const desc = meta?.decoderConfig?.description
  if (desc == null) return false
  const size = (desc as ArrayBufferView).byteLength ?? (desc as ArrayBuffer).byteLength
  return typeof size === 'number' && size > 0
}

/** Очередь дорожек: сначала все профили H.264, следом запасные WebM.
 *
 *  ПОРЯДОК НЕ СИММЕТРИЧЕН. MP4 открывается везде без разговоров — его и просим
 *  сначала, чтобы записанное на одной машине смотрели на любой другой. WebM
 *  берём, только когда H.264 не вышел: Firefox на Linux отвергает все четыре
 *  профиля `avc1` (WebCodecs там опирается на системный кодировщик, а H.264
 *  Mozilla не поставляет). До этого такому человеку кнопка просто отказывала.
 *
 *  Это именно ОЧЕРЕДЬ, а не выбор. Слово браузера («умею avc1») ничего не
 *  решает: Firefox на WINDOWS отвечает согласием, кодирует — и не отдаёт avcC,
 *  а без него mp4 не собрать. Вердикт выносится по первому чанку БОЕВОГО
 *  кодировщика, и запись переезжает на следующую дорожку очереди прямо посреди
 *  первого кадра (см. `exportMp4`). Отдельной пробы больше нет: второй
 *  экземпляр кодировщика сам под подозрением — именно он и соврал бы.
 */
export async function trackPlan(
  supported: (codec: string) => Promise<boolean>,
  mp4Candidates: readonly string[] = CODEC_CANDIDATES,
  webmCandidates: readonly string[] = WEBM_CODEC_CANDIDATES,
): Promise<VideoTrack[]> {
  const plan: VideoTrack[] = []
  for (const codec of mp4Candidates) {
    if (await supported(codec)) plan.push({ codec, container: 'mp4' })
  }
  for (const codec of webmCandidates) {
    if (await supported(codec)) plan.push({ codec, container: 'webm' })
  }
  return plan
}

/** Имя файла под контейнер.
 *
 *  Расширение меняем, а не дописываем: `вид.mp4.webm` система откроет чем
 *  попало. Режем по ПОСЛЕДНЕЙ точке — в имени вида точка встречается своя
 *  («иваси 0.5 сут»).
 */
export function withExtension(fileName: string, container: Container): string {
  const cut = fileName.lastIndexOf('.')
  const base = cut > 0 ? fileName.slice(0, cut) : fileName
  return `${base}.${container}`
}

/** Битрейт под размер кадра, бит/с. Зажат снизу (мелкий кадр не должен
 *  рассыпаться) и сверху (двухсекундный ролик обязан оставаться пересылаемым). */
export function videoBitrate(width: number, height: number): number {
  const raw = width * height * VIDEO_FPS * 0.1
  return Math.round(Math.min(16_000_000, Math.max(2_000_000, raw)))
}

/** Имеет ли смысл жать кнопку и почему нет. */
export function videoPlan(
  anim: { ready: boolean } | undefined,
  supported: boolean,
): { record: true } | { record: false; reason: 'no-animation' | 'unsupported' } {
  if (!anim?.ready) return { record: false, reason: 'no-animation' }
  if (!supported) return { record: false, reason: 'unsupported' }
  return { record: true }
}

/** Умеет ли браузер кодировать видео. */
export function videoSupported(): boolean {
  return typeof globalThis.VideoEncoder !== 'undefined'
}

/** Кодировщик и контейнер одной дорожки, пока идёт запись.
 *
 *  Первый чанк ПРИДЕРЖИВАЕТСЯ: пока не вынесен вердикт, в муксер не уходит
 *  ничего. Иначе вердикт был бы бесполезен — mp4-muxer падает ровно на приёме
 *  чанка без avcC, и «переехать» после этого уже некуда.
 */
interface RecordSession {
  track: VideoTrack
  encoder: VideoEncoder
  muxer: { addVideoChunk: (chunk: unknown, meta: unknown) => void; finalize: () => void
           target: { buffer: ArrayBuffer } }
  /** Метаданные первого чанка — по ним и решается судьба дорожки. */
  firstMeta: ChunkMeta | null
  /** Кодировщик или контейнер отказали: запись обязана кончиться честно. */
  broke: boolean
  /** Вердикт вынесен, чанки идут прямо в контейнер. */
  settled: boolean
  /** Придержанное до вердикта. */
  held: Array<[unknown, unknown]>
}

function makeMuxer(track: VideoTrack, size: { width: number; height: number }) {
  if (track.container === 'webm') {
    return new WebmMuxer({
      target: new WebmArrayBufferTarget(),
      video: {
        codec: track.codec.startsWith('vp09') ? 'V_VP9' : 'V_VP8',
        width: size.width,
        height: size.height,
        frameRate: VIDEO_FPS,
      },
    })
  }
  return new Muxer({
    target: new ArrayBufferTarget(),
    video: { codec: 'avc', width: size.width, height: size.height, frameRate: VIDEO_FPS },
    fastStart: 'in-memory',
  })
}

/** Открыть дорожку: контейнер, кодировщик и придержанный первый чанк. */
export function openSession(
  track: VideoTrack,
  size: { width: number; height: number },
  onDetail?: (detail: string) => void,
): RecordSession {
  const session = {
    track,
    muxer: makeMuxer(track, size),
    firstMeta: null,
    broke: false,
    settled: false,
    held: [],
  } as unknown as RecordSession

  session.encoder = new VideoEncoder({
    // Муксер зовётся ИЗ колбэка кодировщика: брошенное здесь исключение уходит
    // в пустоту (его некому поймать — стек чужой), и запись заканчивалась
    // «успехом» с пустым контейнером. Ловим и помечаем отказ.
    output: (chunk, meta) => {
      if (session.firstMeta === null) session.firstMeta = meta as ChunkMeta
      if (!session.settled) {
        session.held.push([chunk, meta])
        return
      }
      try {
        session.muxer.addVideoChunk(chunk, meta)
      } catch (err) {
        session.broke = true
        onDetail?.(failureDetail({ stage: 'mux', size, codec: track.codec, error: err }))
      }
    },
    // Ошибку СОХРАНЯЕМ. Раньше колбэк ронял её на пол, и отказ на чужой машине
    // становился неотличим ни от чего: консоль не наша, следа не остаётся.
    error: err => {
      session.broke = true
      onDetail?.(failureDetail({ stage: 'encode', size, codec: track.codec, error: err }))
    },
  })
  return session
}

/** Годна ли дорожка по первому чанку боевого кодировщика.
 *
 *  Для mp4 годность = наличие avcC (SPS/PPS): без него дорожка невоспроизводима,
 *  а муксер валится с TypeError. WebM приватных данных кодека не требует, и
 *  спрашивать с него нечего.
 */
export function sessionUsable(track: VideoTrack, meta: ChunkMeta | null): boolean {
  if (track.container !== 'mp4') return true
  return avcMetaUsable(meta)
}

/** Выпустить придержанное в контейнер и открыть ему дорогу дальше. */
function settle(session: RecordSession, size: { width: number; height: number },
                onDetail?: (detail: string) => void): void {
  session.settled = true
  for (const [chunk, meta] of session.held) {
    try {
      session.muxer.addVideoChunk(chunk, meta)
    } catch (err) {
      session.broke = true
      onDetail?.(failureDetail({
        stage: 'mux', size, codec: session.track.codec, error: err }))
    }
  }
  session.held = []
}

/** Пройти кадры ролика: нарисовать кадр — дождаться карты — снять его.
 *
 *  `capture` вправе вернуть `false` и оборвать проход. Нужно ровно для одного
 *  случая: на первом кадре выяснилось, что писать нечем (ни одна дорожка не
 *  собирается в файл). Рисовать после этого ещё полторы сотни кадров — минуты
 *  работы в пустоту.
 */
export async function recordFrames<T>(
  frames: T[],
  render: (frame: T) => Promise<void>,
  capture: (index: number) => void | boolean | Promise<void | boolean>,
  onProgress?: (share: number) => void,
): Promise<void> {
  for (let i = 0; i < frames.length; i++) {
    await render(frames[i])
    const go = await capture(i)
    onProgress?.((i + 1) / frames.length)
    if (go === false) return
  }
}

/** Записать один цикл анимации усиков и скачать MP4. */
export async function exportMp4(
  getCanvas: () => HTMLCanvasElement | null,
  info: ExportContext,
  fileName: string,
  anim: ExportAnimation,
  onProgress?: (share: number) => void,
  /** Снимок координатной сетки. Считается один раз на всю запись: карту во
   *  время неё не двигают (об этом кнопка и предупреждает), а деления зависят
   *  только от вида. */
  grat: GraticuleFrame | null = null,
  /** Куда сложить причину отказа (см. `failureDetail`). При успехе молчит. */
  onDetail?: (detail: string) => void,
  /** Чем в итоге записали. Кнопка называется MP4, и человек вправе знать, что
   *  на его браузере файл вышел другим — иначе «скачал MP4, а лежит .webm». */
  onContainer?: (container: Container) => void,
  /** Куда ушло время — одной строкой (см. `exportTiming.timingSummary`).
   *
   *  Стадии записи имеют разную природу: подготовка суток ждёт чужую очередь на
   *  сервере, цикл кадров ждёт карту, кодировщик считает. Слитые в одну полоску,
   *  они отвечают на вопрос «почему так долго» догадкой — и догадка тут особенно
   *  дешева, потому что у каждой стадии свой правдоподобный потолок. */
  onTiming?: (summary: string) => void,
): Promise<VideoResult> {
  if (!videoSupported()) return 'unsupported'
  const source = getCanvas()
  if (!source) return 'no-frames'
  // Знак подложки декодируется ОДИН раз на всю запись: `composeFrame` синхронен
  // и зовётся на каждый кадр — ждать там нечего и некогда. Кадры ролика уходят
  // из кабинета наружу так же, как PNG, и подписаны обязаны быть так же.
  await prepareAttribution(activeBasemap(), getLang())

  // Часы заводятся ДО выбора дорожки: перебор профилей кодировщика тоже стадия,
  // и на машине, где H.264 отвергается четырежды, она не бесплатна.
  const clock = makeClock()

  const size = videoSize(source.width, source.height)
  const supported = async (c: string) => {
    try {
      const { supported: ok } = await VideoEncoder.isConfigSupported({
        codec: c,
        width: size.width,
        height: size.height,
      })
      return Boolean(ok)
    } catch {
      return false
    }
  }
  // Очередь дорожек, а не выбор: годность решается по первому чанку боевого
  // кодировщика, и запись переезжает на следующую прямо посреди первого кадра.
  const plan = await clock.measure('codec', () => trackPlan(supported))
  if (!plan.length) {
    onDetail?.(failureDetail({ stage: 'codec', size }))
    return 'unsupported'
  }

  const configureFor = (session: RecordSession): boolean => {
    try {
      session.encoder.configure({
        codec: session.track.codec,
        width: size.width,
        height: size.height,
        framerate: VIDEO_FPS,
        bitrate: videoBitrate(size.width, size.height),
        latencyMode: 'quality',
      })
      return true
    } catch (err) {
      // Настройка бросает ДО начала записи — падать наружу нельзя: снаружи
      // исключение читалось бы как «попробуйте ещё раз».
      onDetail?.(failureDetail({
        stage: 'configure', size, codec: session.track.codec, error: err }))
      if (session.encoder.state !== 'closed') session.encoder.close()
      return false
    }
  }

  let planIndex = 0
  let session = openSession(plan[planIndex], size, onDetail)
  if (!configureFor(session)) return 'unsupported'

  // Одно полотно на всю запись: VideoFrame снимает копию пикселей, поэтому
  // кадры не наступают друг другу на пятки, а мусора на полторы сотни полотен нет.
  const stage = document.createElement('canvas')
  // Подпись первого и среднего кадров: если они совпали, движения в записи нет
  // (кометы не пошли, точки не поехали) — файл с такой «анимацией» отдавать нельзя.
  let first: Uint8ClampedArray | null = null
  let middle: Uint8ClampedArray | null = null

  const encodeStage = (index: number) => {
    const frame = new VideoFrame(stage, {
      timestamp: frameTimestampUs(index),
      duration: Math.round(1e6 / VIDEO_FPS),
    })
    session.encoder.encode(frame, { keyFrame: index === 0 })
    frame.close()
  }

  /** Вердикт по первому чанку: остаёмся или переезжаем на следующую дорожку.
   *
   *  Решается ЗДЕСЬ, после первого кадра, а не после `flush()` всей записи:
   *  подготовка суток (`anim.begin`) стоит до минуты и пересдаче не подлежит, а
   *  переписать один кадр — это один вызов кодировщика. Пиксели первого кадра
   *  всё ещё лежат на общем полотне, второй кадр ещё не рисовался.
   *
   *  Возвращает `false`, если писать больше нечем: очередь дорожек кончилась.
   */
  const decideTrack = async (): Promise<boolean> => {
    // Частичный flush выпускает первый чанк — дальше кодировщик работает как ни
    // в чём не бывало. Ждать полного flush записи нельзя: к тому времени
    // муксер уже принял бы негодный чанк и упал.
    await session.encoder.flush()
    while (!sessionUsable(session.track, session.firstMeta) || session.broke) {
      onDetail?.(failureDetail({
        stage: 'probe', size, codec: session.track.codec,
        error: session.broke ? 'encoder-failed' : 'no-avcc',
      }))
      if (session.encoder.state !== 'closed') session.encoder.close()
      planIndex += 1
      if (planIndex >= plan.length) return false
      session = openSession(plan[planIndex], size, onDetail)
      if (!configureFor(session)) return false
      encodeStage(0)
      await session.encoder.flush()
    }
    settle(session, size, onDetail)
    return true
  }

  try {
    // Подготовка суток: дорисовка недостающих дат идёт последовательно и ждёт
    // серверную очередь. Именно её человек видит как «Запись 0%», и именно она
    // способна занять минуты — остальное измеряется секундами.
    const frames = await clock.measure('prepare', () => anim.begin(onProgress))
    if (!frames.length) {
      // Готовых суток нет вовсе. Не «no-frames»: кодировщик тут ни при чём, и
      // повтор ничего не изменит — см. exportNote.
      anim.end()
      if (session.encoder.state !== 'closed') session.encoder.close()
      return 'no-dates'
    }
    const middleIndex = Math.floor(frames.length / 2)
    let usable = true
    await recordFrames(
      frames,
      // `render` — это рисование кадра И ожидание карты (`waitMapIdle`): на
      // смене суток там приезжают новые тайлы, и потолок втрое выше. Разделять
      // их незачем — лечатся они одним и тем же.
      frame => clock.measure('render', () => anim.renderFrame(frame)),
      async index => {
        const map = getCanvas()
        if (!map) return
        const timer = anim.timerFor(frames[index])
        // Дата в угловой плашке едет за кадром: ролик идёт по суткам, и
        // застывшая дата врала бы всю запись, кроме одного кадра.
        const frameInfo = timer ? { ...info, date: timer.date } : info
        clock.measureSync('compose', () => {
          composeFrame(map, frameInfo, size.width / map.width, grat, stage, timer)
          if (index === 0) first = framePixels(stage)
          else if (index === middleIndex) middle = framePixels(stage)
        })
        clock.measureSync('encode', () => encodeStage(index))
        if (index === 0) {
          usable = await clock.measure('track', decideTrack)
          // Писать нечем — обрываем проход: рисовать ещё полторы сотни кадров
          // в пустоту дороже честного отказа.
          return usable
        }
        // Подпор очереди: кодировщик отстаёт — даём ему такт, иначе к концу
        // записи в памяти висит весь ролик несжатым. Считается ОТДЕЛЬНО от
        // самого кодирования: это ожидание, и лечится оно битрейтом или
        // размером кадра, а не скоростью отрисовки.
        await clock.measure('encode-wait', async () => {
          while (session.encoder.encodeQueueSize > 8) {
            await new Promise(res => setTimeout(res, 5))
          }
        })
      },
      onProgress,
    )
    if (!usable) {
      // Ни одна дорожка очереди не собралась в файл — это отказ браузера, а не
      // сбой записи: повтор ничего не изменит (см. exportNote).
      return 'unsupported'
    }
    await clock.measure('flush', async () => {
      await session.encoder.flush()
      session.muxer.finalize()
    })
    if (session.broke) return 'failed'
    if (first && middle && !pixelsDiffer(first, middle)) return 'no-motion'
    const buffer = session.muxer.target.buffer
    if (!buffer.byteLength) {
      onDetail?.(failureDetail({
        stage: 'save', size, codec: session.track.codec, error: 'empty-container' }))
      return 'no-frames'
    }
    download(
      new Blob([buffer], { type: `video/${session.track.container}` }),
      withExtension(fileName, session.track.container),
    )
    onContainer?.(session.track.container)
    return 'saved'
  } catch (err) {
    onDetail?.(failureDetail({
      stage: 'encode', size, codec: session.track.codec, error: err }))
    throw err
  } finally {
    anim.end()
    if (session.encoder.state !== 'closed') session.encoder.close()
    // Отчёт отдаётся ВСЕГДА, в том числе при отказе: запись, упавшая на четвёртой
    // минуте, — как раз тот случай, когда знать распределение времени нужнее
    // всего. Итог берём здесь же, чтобы в него вошли все стадии.
    const summary = timingSummary(clock.report(), clock.total())
    if (summary) onTiming?.(summary)
  }
}
